/**
 * TanStack Query hooks over the proxy. staleTimes mirror the server-side TTLs so
 * the browser and the proxy cache agree on freshness (the proxy is the real
 * budget guard; these just avoid redundant refetches within a view).
 */
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  AccountBalances,
  AccountHistory,
  AccountPartners,
  AccountPositions,
  Arbitrage,
  Signal,
  AuthMe,
  BalanceRefresh,
  Contest,
  ContestState,
  ContestStateBatch,
  Depth,
  HistorySync,
  League,
  LiquidityPosition,
  Market,
  Opportunity,
  Order,
  OrderResolution,
  Partner,
  Performance,
  PriceHistory,
  RestingOrders,
} from "@greenbook/client";
import type { BuyLeg, BuyResponse, CancelResponse } from "@openmarketsai/order-sheet";
import { ApiError, apiGet, apiPost, type Envelope } from "./api.js";
import { SIGN_IN_REQUIRED } from "./publicFeed.js";
import { getOrderClient } from "./orderClient.js";
import { useLiveEnabled } from "./ws.js";
import { SCOPES, useAccess } from "./omauth.js";
import { trimForCard, type CardLiquidity } from "./marketsBoard.js";
import { trimForHoldings } from "./portfolio.js";
import { boardUrl } from "./publicBoard.js";

/**
 * May this query run for the current visitor? Signed out, only the public-feed reads run
 * (leagues, partners, contests, markets, liquidity — they carry no gate). Everything else
 * waits for a credential, and — for an SDK session — for the specific granted scope, so a
 * signed-out or declined-scope session never fires a doomed authenticated call. (apiGet also
 * refuses off-list paths signed out; this keeps the query idle instead of erroring.)
 */
function useGate(scope?: string): boolean {
  return useAccess(scope).ok;
}

const MIN = 60 * 1000;

// When the WS relay is connected, REST polling drops to a slow safety-net: for
// liquidity the relay pushes cover changes, so 5m is ample. For contest state the
// live push channel is unverified (no pregame pushes observed — see FRICTION.md),
// so we keep a more conservative 90s net even when "connected".
const LIVE_LIQUIDITY_MS = 5 * MIN;
const LIVE_STATE_MS = 90 * 1000;

export function useAuthMe(): UseQueryResult<AuthMe> {
  const allowed = useGate();
  return useQuery({
    queryKey: ["auth", "me"],
    enabled: allowed,
    queryFn: async ({ signal }) => (await apiGet<AuthMe>("/auth/me", signal)).data,
    staleTime: 5 * MIN,
    refetchInterval: 5 * MIN,
  });
}

export function useLeagues(): UseQueryResult<League[]> {
  return useQuery({
    queryKey: ["leagues"],
    queryFn: async ({ signal }) => (await apiGet<League[]>("/leagues", signal)).data,
    staleTime: 60 * MIN,
  });
}

export function usePartners(): UseQueryResult<Partner[]> {
  return useQuery({
    queryKey: ["partners"],
    queryFn: async ({ signal }) => (await apiGet<Partner[]>("/partners", signal)).data,
    staleTime: 60 * MIN,
  });
}

/** Client capability flags the UI reads to hide toggles (real/paper is the global trading mode). */
export interface ServerConfig {
  enable_limit_orders: boolean;
}
export function useServerConfig(): UseQueryResult<ServerConfig> {
  return useQuery({
    queryKey: ["config"],
    queryFn: async ({ signal }) => (await apiGet<ServerConfig>("/config", signal)).data,
    staleTime: 60 * MIN,
  });
}

export interface ContestFilter {
  leagueId?: string;
  status?: string;
  /** Server-side title search (#860): case-insensitive substring, 2–100 chars. Composes
   *  with league/status and paginates on the same cursor. Undefined = no search. */
  q?: string;
  /** Page size (API max 200). Default 50. */
  limit?: number;
}

export function useContests(
  filter: ContestFilter,
): UseInfiniteQueryResult<{ pages: Envelope<Contest[]>[] }, Error> {
  const { leagueId, status, q, limit = 50 } = filter;
  return useInfiniteQuery({
    queryKey: ["contests", leagueId ?? null, status ?? null, q ?? null, limit],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const qs = new URLSearchParams();
      if (leagueId) qs.set("league_id", leagueId);
      if (status) qs.set("status", status);
      if (q) qs.set("q", q);
      qs.set("limit", String(limit));
      // `next_cursor` is an OPAQUE composite string (#859) — pass it back verbatim, never parse.
      if (pageParam) qs.set("cursor", pageParam);
      return apiGet<Contest[]>(`/contests?${qs.toString()}`, signal);
    },
    getNextPageParam: (last) =>
      last.pagination?.has_more && last.pagination.next_cursor
        ? last.pagination.next_cursor
        : undefined,
    staleTime: MIN,
  });
}

export function useContest(id: string | undefined): UseQueryResult<Contest> {
  return useQuery({
    queryKey: ["contest", id],
    enabled: !!id,
    queryFn: async ({ signal }) => (await apiGet<Contest>(`/contests/${id}`, signal)).data,
    staleTime: 5 * MIN,
  });
}

export function useContestMarkets(id: string | undefined): UseQueryResult<Market[]> {
  return useQuery({
    queryKey: ["markets", id],
    enabled: !!id,
    queryFn: async ({ signal }) =>
      (await apiGet<Market[]>(`/contests/${id}/markets`, signal)).data,
    staleTime: 5 * MIN,
  });
}

/** Set once the same-origin public liquidity passthrough proves absent; then go direct. */
let feedLiquidityUnavailable = false;

/**
 * A game's full liquidity. First choice is greenbook's own public feed
 * (`/api/public/contests/:id/liquidity`, org key, same-origin), even when signed in: the API
 * sends a busy game's 3.3 MB uncompressed (FRICTION.md), which on an ordinary connection takes
 * seconds and stalls every other call behind it. Through the feed it's brotli-compressed at the
 * edge (production) or a localhost hop from the dev server. Prices are public market data, the
 * same for every viewer; Real-mode venue scoping stays client-side. Falls back to the direct
 * call (the viewer's own key) when the feed isn't there.
 */
export async function fetchLiquidity(id: string, signal?: AbortSignal): Promise<LiquidityPosition[]> {
  if (!feedLiquidityUnavailable) {
    try {
      const res = await window.fetch(`/api/public/contests/${encodeURIComponent(id)}/liquidity`, {
        headers: { Accept: "application/json" },
        signal,
      });
      const isJson = (res.headers.get("content-type") ?? "").includes("json");
      if (res.ok && isJson) {
        const rows = ((await res.json()) as { data?: unknown }).data;
        if (Array.isArray(rows)) return rows as LiquidityPosition[];
      }
      // No feed here (the SPA's index.html, a 404, or an unconfigured key): stop trying it.
      if (!isJson || res.status === 404 || res.status === 503) feedLiquidityUnavailable = true;
    } catch (e) {
      if (signal?.aborted) throw e;
    }
  }
  return (await apiGet<LiquidityPosition[]>(`/contests/${id}/liquidity`, signal)).data ?? [];
}

export function useLiquidity(id: string | undefined): UseQueryResult<LiquidityPosition[]> {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["liquidity", id],
    enabled: !!id,
    queryFn: ({ signal }) => fetchLiquidity(id!, signal),
    staleTime: 30 * 1000,
    refetchInterval: live ? LIVE_LIQUIDITY_MS : 30 * 1000,
  });
}

/* ---------------------------------------------------- Markets board card prices */

/**
 * At most this many card liquidity requests in flight at once. A board lazy-loads one
 * `GET /contests/:id/liquidity` per card as it nears the viewport; a fast scroll would otherwise
 * fire dozens at once (and a busy game's payload is ~3 MB — see FRICTION.md).
 */
export const CARD_LIQUIDITY_CONCURRENCY = 4;
let cardInFlight = 0;
const cardQueue: (() => void)[] = [];

function acquireCardSlot(signal?: AbortSignal): Promise<void> {
  if (cardInFlight < CARD_LIQUIDITY_CONCURRENCY) {
    cardInFlight++;
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const go = () => {
      signal?.removeEventListener("abort", onAbort);
      cardInFlight++;
      resolve();
    };
    const onAbort = () => {
      const i = cardQueue.indexOf(go);
      if (i >= 0) cardQueue.splice(i, 1);
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    cardQueue.push(go);
  });
}
function releaseCardSlot(): void {
  cardInFlight--;
  cardQueue.shift()?.();
}

/** Visible cards without a live stream re-read about this often (the public feed edge-caches ~30–60 s). */
export const CARD_POLL_MS = 60 * 1000;

/**
 * One Markets card's prices: the contest's liquidity, trimmed to the card's market (see
 * `trimForCard`) so the board doesn't hold megabytes per game. Keyed under ["liquidity", id] so
 * the stream bridge patches it and a `refresh_required` invalidates it alongside the game page.
 *
 * `enabled` flips on once the card nears the viewport and never back (no refetch while
 * scrolling); `poll` is on only while the card is on screen and not covered by the stream.
 * No focus/reconnect refetch: that would re-read every card the user has ever scrolled past.
 */
/** Once the board route has shown it isn't served here (static host, dev without a key), stop asking. */
let boardUnavailable = false;

/**
 * A card's prices. First choice is the public board route (`/api/public/contests/:id/board`,
 * lib/publicBoard.ts): trimmed and edge-cached server-side, a few KB instead of the full
 * multi-MB liquidity, and off the viewer's own rate limit. Card prices are public, so signed-in
 * viewers use it too. Falls back to the full liquidity (signed in: direct; signed out: the
 * public passthrough) and the same `trimForCard` when the board isn't available.
 */
async function fetchCardLiquidity(id: string, game: boolean, signal?: AbortSignal): Promise<CardLiquidity> {
  if (!boardUnavailable) {
    try {
      const res = await window.fetch(boardUrl(id, game ? "game" : "futures"), { headers: { Accept: "application/json" }, signal });
      const isJson = (res.headers.get("content-type") ?? "").includes("json");
      if (res.ok && isJson) return ((await res.json()) as { data: CardLiquidity }).data;
      // No route here (the SPA's index.html, a 404, or an unconfigured feed): don't retry it.
      if (!isJson || res.status === 404 || res.status === 503) boardUnavailable = true;
    } catch (e) {
      if (signal?.aborted) throw e;
    }
  }
  return trimForCard(await fetchLiquidity(id, signal), game);
}

export function useCardLiquidity(
  id: string,
  game: boolean,
  opts: { enabled: boolean; poll: boolean },
): UseQueryResult<CardLiquidity> {
  return useQuery({
    queryKey: ["liquidity", id, "card"],
    enabled: opts.enabled,
    queryFn: async ({ signal }) => {
      await acquireCardSlot(signal);
      try {
        return await fetchCardLiquidity(id, game, signal);
      } finally {
        releaseCardSlot();
      }
    },
    staleTime: CARD_POLL_MS - 5 * 1000,
    refetchInterval: opts.poll ? CARD_POLL_MS : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
}

/**
 * One Portfolio game's prices: the contest's liquidity trimmed to the held sides and the other
 * side of each (see `trimForHoldings`), for Value now, "price now" and the Hedge ticket. Same
 * budget rules as a Markets card — shares its in-flight cap, lazy (`enabled` flips on near the
 * viewport), polls only while on screen and not streamed — and is keyed under
 * ["liquidity", id] so the stream bridge patches it in place.
 *
 * Reuses the Markets card cache both ways: when the card's trimmed moneyline already holds every
 * held side, that's the starting data (no request); and a fetch here seeds the card cache, so
 * Markets doesn't re-download the game.
 */
export function usePortfolioLiquidity(
  id: string,
  game: boolean,
  heldHashes: string[],
  opts: { enabled: boolean; poll: boolean },
): UseQueryResult<{ positions: LiquidityPosition[] }> {
  const qc = useQueryClient();
  const hashes = [...new Set(heldHashes)].sort();
  const cardKey = ["liquidity", id, "card"];
  const card = qc.getQueryData<CardLiquidity>(cardKey);
  const fromCard =
    card && hashes.length > 0 && hashes.every((h) => card.positions.some((p) => p.position_hash === h))
      ? { positions: card.positions as LiquidityPosition[] }
      : undefined;
  return useQuery({
    queryKey: ["liquidity", id, "portfolio", hashes.join(",")],
    enabled: opts.enabled && hashes.length > 0,
    queryFn: async ({ signal }) => {
      await acquireCardSlot(signal);
      try {
        const rows = await fetchLiquidity(id, signal);
        if (!qc.getQueryData(cardKey)) qc.setQueryData(cardKey, trimForCard(rows, game));
        return trimForHoldings(rows, hashes) as { positions: LiquidityPosition[] };
      } finally {
        releaseCardSlot();
      }
    },
    initialData: fromCard,
    initialDataUpdatedAt: fromCard ? qc.getQueryState(cardKey)?.dataUpdatedAt : undefined,
    // A new fill on the same game changes the key; keep showing the old prices meanwhile.
    placeholderData: keepPreviousData,
    staleTime: CARD_POLL_MS - 5 * 1000,
    refetchInterval: opts.poll ? CARD_POLL_MS : false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
}

export function useContestState(id: string | undefined): UseQueryResult<ContestState | null> {
  const live = useLiveEnabled();
  const allowed = useGate(SCOPES.gamestate);
  return useQuery({
    queryKey: ["state", id],
    enabled: allowed && !!id,
    queryFn: async ({ signal }) =>
      (await apiGet<ContestState | null>(`/contests/${id}/state`, signal)).data,
    staleTime: 15 * 1000,
    refetchInterval: live ? LIVE_STATE_MS : 15 * 1000,
  });
}

/** Batch live state for a set of visible contests (browser live chips). */
export function useContestStateBatch(ids: string[]): UseQueryResult<ContestStateBatch> {
  const live = useLiveEnabled();
  const key = [...ids].sort();
  const allowed = useGate(SCOPES.gamestate);
  return useQuery({
    queryKey: ["state-batch", key],
    enabled: allowed && ids.length > 0,
    queryFn: async ({ signal }) =>
      (await apiPost<ContestStateBatch>("/contest-state/batch", { contest_ids: ids }, signal)).data,
    staleTime: 15 * 1000,
    refetchInterval: live ? LIVE_STATE_MS : 20 * 1000,
  });
}

export function useDepth(hash: string | undefined, opts: { refetchMs?: number } = {}): UseQueryResult<Depth> {
  const allowed = useGate(SCOPES.depth);
  return useQuery({
    queryKey: ["depth", hash],
    enabled: allowed && !!hash,
    refetchInterval: opts.refetchMs ?? false,
    queryFn: async ({ signal }) =>
      (await apiGet<Depth>(`/depth/${encodeURIComponent(hash!)}`, signal)).data,
    staleTime: 10 * 1000,
  });
}

/* ------------------------------------------------ account & execution layer */
// TTLs mirror the server-side cache (30s account data, 60s price history). A slow
// safety-net refetch keeps balances/positions live while the desk is open.

const ACCOUNT_MS = 30 * 1000;

export function useAccountPartners(): UseQueryResult<AccountPartners> {
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["account", "partners"],
    enabled: allowed,
    queryFn: async ({ signal }) =>
      (await apiGet<AccountPartners>("/account/partners", signal)).data,
    staleTime: ACCOUNT_MS,
    refetchInterval: ACCOUNT_MS,
  });
}

export function useAccountBalances(): UseQueryResult<AccountBalances> {
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["account", "balances"],
    enabled: allowed,
    queryFn: async ({ signal }) =>
      (await apiGet<AccountBalances>("/account/balances", signal)).data,
    staleTime: ACCOUNT_MS,
    refetchInterval: ACCOUNT_MS,
  });
}

// The unfiltered ("all") view is proxy-paged over the whole lifetime book (~10.8k
// rows; see proxy.ts), so poll it much slower to keep the paging burst rare. A
// status-filtered view (open/settled) is a cheap server-side slice — `open` is a
// handful of rows — so it can poll at the normal account cadence. A buy/sync
// invalidation still forces an immediate refetch when it matters.
const POSITIONS_MS = 5 * 60 * 1000;

/**
 * Positions rollup. Pass `status` (`open`/`settled`) for the fast server-side slice
 * (live 2026-07-15) — `open` is instant, no whole-book walk. Omit it for the full
 * lifetime book (the pre-warmed proxy store).
 */
export function usePositions(status?: string): UseQueryResult<AccountPositions> {
  const filtered = !!status;
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["account", "positions", status ?? "all"],
    enabled: allowed,
    queryFn: async ({ signal }) => {
      const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
      return (await apiGet<AccountPositions>(`/account/positions${suffix}`, signal)).data;
    },
    staleTime: filtered ? ACCOUNT_MS : POSITIONS_MS,
    refetchInterval: filtered ? ACCOUNT_MS : POSITIONS_MS,
  });
}

/**
 * OM-native P&L analytics (`GET /auth/performance`). Fresh — unlike the raw venue
 * ledger — so this is the basis for KPIs and the P&L calendar. `groupBy=none` gives
 * portfolio totals; `groupBy=day` gives per-day buckets over the [from,to] window.
 */
export function usePerformance(params: {
  groupBy?: string;
  from?: string;
  to?: string;
  enabled?: boolean;
}): UseQueryResult<Performance> {
  const { groupBy, from, to, enabled = true } = params;
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["performance", groupBy ?? null, from ?? null, to ?? null],
    enabled: allowed && enabled,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      if (groupBy) qs.set("group_by", groupBy);
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return (await apiGet<Performance>(`/performance${suffix}`, signal)).data;
    },
    staleTime: ACCOUNT_MS,
    refetchInterval: ACCOUNT_MS,
  });
}

/**
 * The open `manual` opportunities with their full `legs[]` (cancelled legs included), which is
 * where Portfolio's plays, the Exposed filter, the nav badge and the Desk read exposure from.
 * Typed `manual` because greenbook's own plays (arbs, quotes, hedges) are all `manual`, and the
 * account's single-leg `auto_market_making` rows would otherwise fill every page (FRICTION.md).
 */
export function useOpenPlayOpportunities(): UseQueryResult<Opportunity[]> {
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["opportunities", "open-manual"],
    enabled: allowed,
    queryFn: async ({ signal }) =>
      (await apiGet<Opportunity[]>(`/opportunities?status=open&opportunity_type=manual&limit=200`, signal)).data,
    staleTime: ACCOUNT_MS,
    refetchInterval: ACCOUNT_MS,
  });
}

export interface OrderFilters {
  currency?: string; // USD | ATLAS
  status?: string;
  settled?: boolean;
  /** `sort=settled` orders by settlement time (newest first) and makes `from` a settled-date bound. */
  sort?: string;
  /** ISO day (YYYY-MM-DD) lower bound on the sort column (see the docs' `from`/`to`). */
  from?: string;
  /** ISO day upper bound on the sort column — EXCLUSIVE (`to=DAY+1` includes DAY). */
  to?: string;
  /** One settled result: win | lose | push | cancelled (the API takes a single value). */
  result?: string;
}

/** Paginated order blotter with optional filter chips (currency/status/settled). */
export function useOrders(
  limit = 50,
  filters: OrderFilters = {},
): UseInfiniteQueryResult<{ pages: Envelope<Order[]>[] }, Error> {
  const { currency, status, settled, sort, from, to, result } = filters;
  const allowed = useGate(SCOPES.account);
  return useInfiniteQuery({
    queryKey: ["orders", limit, currency ?? null, status ?? null, settled ?? null, sort ?? null, from ?? null, to ?? null, result ?? null],
    enabled: allowed,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const qs = new URLSearchParams();
      qs.set("limit", String(limit));
      if (currency) qs.set("currency", currency);
      if (status) qs.set("status", status);
      if (settled) qs.set("settled", "true");
      if (sort) qs.set("sort", sort);
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      if (result) qs.set("result", result);
      if (pageParam) qs.set("cursor", pageParam);
      return apiGet<Order[]>(`/orders?${qs.toString()}`, signal);
    },
    getNextPageParam: (last) =>
      last.pagination?.has_more && last.pagination.next_cursor
        ? last.pagination.next_cursor
        : undefined,
    staleTime: ACCOUNT_MS,
  });
}

/** The next calendar day (ISO YYYY-MM-DD) after `day`, computed in UTC. */
function nextDayIso(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/**
 * Orders SETTLED on a single day, for the P&L-calendar day-detail popup. `currency`
 * scopes to the calendar's currency (USD real / ATLAS play). 60s cache.
 *
 * The settled-date filter went live 2026-07-15, so this is now ONE scoped request:
 * `settled=true&from=DAY&to=DAY+1&sort=settled` (the `to` boundary is EXCLUSIVE, so
 * a single day is `from=DAY`, `to=DAY+1`). No more paging the recency feed and
 * bucketing client-side.
 */
export function useDayOrders(
  day: string | null,
  currency: string,
): UseQueryResult<Order[]> {
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["orders", "day", day, currency],
    enabled: allowed && !!day,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      qs.set("settled", "true");
      qs.set("from", day!);
      qs.set("to", nextDayIso(day!)); // `to` is an EXCLUSIVE next-day boundary
      qs.set("sort", "settled");
      qs.set("limit", "200");
      if (currency) qs.set("currency", currency);
      return (await apiGet<Order[]>(`/orders?${qs.toString()}`, signal)).data;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Resting (working) orders. Auto-refreshes every ~15s while any exist; polling
 * stops once the list is empty (nothing to watch). Filled orders drop off.
 */
export function useRestingOrders(): UseQueryResult<RestingOrders> {
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["orders", "resting"],
    enabled: allowed,
    queryFn: async ({ signal }) => (await apiGet<RestingOrders>("/orders/resting", signal)).data,
    staleTime: 3 * 1000,
    refetchInterval: (q) => ((q.state.data?.orders?.length ?? 0) > 0 ? 15 * 1000 : false),
  });
}

/**
 * Cross-venue arbitrages (needs `arbitrage:read`). Seeds the flat `["arbitrages"]` cache
 * that the WS `arbitrage.update` pushes merge into; filtering happens client-side. Broad
 * fetch (the UI filters), short poll as a fallback when the WS is down.
 */
export function useArbitrages(): UseQueryResult<Arbitrage[]> {
  const allowed = useGate();
  return useQuery({
    queryKey: ["arbitrages"],
    enabled: allowed,
    queryFn: async ({ signal }) =>
      (await apiGet<Arbitrage[]>("/arbitrages?limit=200&sort=after_fee_roi", signal)).data,
    staleTime: 5 * 1000,
    refetchInterval: 20 * 1000,
  });
}

/**
 * Live signals feed. REST gives the opening set; the `signal` WS channel patches the
 * ["signals"] cache per contest (see applySignalUpdate in ws.ts). The 30s poll is a backstop
 * that also drops expired signals the stream didn't explicitly clear.
 */
export function useSignals(): UseQueryResult<Signal[]> {
  const allowed = useGate();
  return useQuery({
    queryKey: ["signals"],
    enabled: allowed,
    queryFn: async ({ signal }) =>
      (await apiGet<Signal[]>("/signals?status=active&limit=200", signal)).data,
    staleTime: 5 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/** Settlement detail for a single order — fetched lazily on row expand. */
export function useResolution(
  routerOrderId: string | undefined,
  enabled: boolean,
): UseQueryResult<OrderResolution> {
  const allowed = useGate(SCOPES.account);
  return useQuery({
    queryKey: ["resolution", routerOrderId],
    enabled: allowed && enabled && !!routerOrderId,
    queryFn: async ({ signal }) =>
      (await apiGet<OrderResolution>(`/orders/${encodeURIComponent(routerOrderId!)}/resolution`, signal))
        .data,
    staleTime: 30 * 1000,
  });
}

/* ------------------------------------------------------------- mutations */

/**
 * After ANY placement or cancel: the blotter, resting list, balances, positions (under
 * `["account", …]`) and opportunities all moved. Shared with the trade ticket, which places
 * through the order SDK's sheet rather than a mutation.
 */
export function invalidateAfterOrder(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ["orders"] });
  void qc.invalidateQueries({ queryKey: ["account"] });
  void qc.invalidateQueries({ queryKey: ["opportunities"] });
}

function requireOrderClient() {
  const om = getOrderClient();
  if (!om) throw new ApiError(401, SIGN_IN_REQUIRED, "Sign in with OpenMarkets to trade.", {});
  return om;
}

/**
 * Multi-leg buy through the order SDK's headless core (`om.buy`). Every leg must carry an
 * explicit `mode` (the SDK refuses one without); a `real` leg is refused by the SDK unless
 * greenbook armed real money (global Real + trade:execute — see orderClient.ts). `atomic`
 * expands to the hedge recipe: check_all_first + halt + parallel + verify_liquidity. The SDK
 * mints the Idempotency-Key and never retries; react-query mutations don't retry either.
 */
export function useOrderBuy(): UseMutationResult<
  BuyResponse,
  Error,
  { legs: BuyLeg[]; atomic: boolean; verifyLiquidity?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ legs, atomic, verifyLiquidity }) => requireOrderClient().buy(legs, { atomic, verifyLiquidity }),
    onSuccess: () => invalidateAfterOrder(qc),
  });
}

/** Cancel one or more resting orders (through the order SDK's client). */
export function useCancelOrders(): UseMutationResult<Envelope<CancelResponse>, Error, string[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => ({ data: await requireOrderClient().cancelOrders(ids) }),
    onSuccess: () => invalidateAfterOrder(qc),
  });
}

/** Re-read live venue balances (slow; user-triggered). */
export function useRefreshBalances(): UseMutationResult<Envelope<BalanceRefresh>, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<BalanceRefresh>("/account/refresh-balances", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account"] });
    },
  });
}

/**
 * Dispatch a re-sync of the raw venue ledger + positions rollup. The dispatch is
 * fire-and-forget server-side (updates land over the next few seconds), so on
 * success we invalidate history + positions and the caller refetches after a delay.
 */
export function useSyncHistory(): UseMutationResult<Envelope<HistorySync>, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<HistorySync>("/account/sync-history", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["account", "positions"] });
    },
  });
}

/** Paginated account history (fills + transactions). */
export function useAccountHistory(
  limit = 50,
): UseInfiniteQueryResult<{ pages: Envelope<AccountHistory>[] }, Error> {
  const allowed = useGate(SCOPES.account);
  return useInfiniteQuery({
    queryKey: ["history", limit],
    enabled: allowed,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const qs = new URLSearchParams();
      qs.set("limit", String(limit));
      if (pageParam) qs.set("cursor", pageParam);
      return apiGet<AccountHistory>(`/account/history?${qs.toString()}`, signal);
    },
    getNextPageParam: (last) =>
      last.pagination?.has_more && last.pagination.next_cursor
        ? last.pagination.next_cursor
        : undefined,
    staleTime: ACCOUNT_MS,
  });
}

export function usePriceHistory(
  id: string | undefined,
  params: {
    resolution?: string;
    limit?: number;
    /** Scope to one outcome (comma-separable per docs; we pass one). */
    position_hash?: string;
    /** Window size; `from`/`to` computed at fetch time (to = now). */
    windowMs?: number;
  } = {},
): UseQueryResult<PriceHistory> {
  const allowed = useGate(SCOPES.pricing);
  return useQuery({
    queryKey: [
      "price-history",
      id,
      params.resolution ?? null,
      params.limit ?? null,
      params.position_hash ?? null,
      params.windowMs ?? null,
    ],
    enabled: allowed && !!id,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      if (params.resolution) qs.set("resolution", params.resolution);
      if (params.limit) qs.set("limit", String(params.limit));
      if (params.position_hash) qs.set("position_hash", params.position_hash);
      if (params.windowMs) {
        const to = Date.now();
        qs.set("from", String(to - params.windowMs));
        qs.set("to", String(to));
      }
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return (await apiGet<PriceHistory>(`/contests/${id}/price-history${suffix}`, signal)).data;
    },
    staleTime: 60 * 1000,
  });
}
